import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCheckCircle, faCopy, faFile, faFlask, faLocationDot, faLink, faEnvelope, 
  faTimesCircle, faLock, faPaperPlane, faClipboardList, faClock, faRotate, 
  faTrash, faXmark, faGlobe, faKey
} from '@fortawesome/free-solid-svg-icons';

const API_BASE_URL = 'http://localhost:5000/api';

interface WebhookLog {
  id: string;
  receivedAt: string;
  method: string;
  headers: any;
  body: any;
  rawBody: string;
  signature?: string;
  signatureVerified?: string;
  queryParams?: any;
  ip?: string;
  error?: string;
}

const WebhookTestPage: React.FC = () => {
  const { t } = useTranslation();
  const [logs, setLogs] = useState<WebhookLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [selectedLog, setSelectedLog] = useState<WebhookLog | null>(null);
  const [webhookUrl, setWebhookUrl] = useState('');
  
  const [testPayload, setTestPayload] = useState(JSON.stringify({
    event: "order.paid",
    order_number: "ORD1728374929123",
    status: "paid",
    base_amount: 100.00,
    total_amount: 100.45,
    customer_name: "นาย A",
    customer_email: "customer@example.com",
    reference_id: "YOUR-REF-123",
    paid_at: new Date().toISOString(),
    created_at: new Date().toISOString(),
    timestamp: new Date().toISOString()
  }, null, 2));

  const [verifyPayload, setVerifyPayload] = useState('');
  const [verifySignature, setVerifySignature] = useState('');
  const [verifySecret, setVerifySecret] = useState('');
  const [verifyResult, setVerifyResult] = useState<any>(null);

  const loadLogs = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/webhook-test/logs?limit=50`);
      setLogs(response.data.data || []);
    } catch (error: any) {
      console.error('Error loading logs:', error);
    }
  }, []);

  useEffect(() => {
    const host = window.location.hostname === 'localhost' 
      ? 'localhost' 
      : window.location.hostname;
    setWebhookUrl(`http://${host}:5000/api/webhook-test/receive`);
    
    loadLogs();
  }, [loadLogs]);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (autoRefresh) {
      interval = setInterval(() => {
        loadLogs();
      }, 3000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh, loadLogs]);

  const clearLogs = async () => {
    if (!window.confirm(t('webhookTest.confirmClearLogs'))) return;
    
    try {
      await axios.delete(`${API_BASE_URL}/webhook-test/logs`);
      setLogs([]);
      setSelectedLog(null);
      toast.success(t('webhookTest.logsCleared'));
    } catch (error: any) {
      toast.error(t('common.error'));
    }
  };

  const sendTestWebhook = async () => {
    if (!webhookUrl) {
      toast.error(t('webhookTest.pleaseEnterUrl'));
      return;
    }

    setLoading(true);
    try {
      const payload = JSON.parse(testPayload);
      
      await axios.post(webhookUrl, payload, {
        headers: {
          'Content-Type': 'application/json',
          'User-Agent': 'WebhookTester/1.0'
        }
      });

      toast.success(t('webhookTest.sendSuccess'));
      setTimeout(() => loadLogs(), 500);
    } catch (error: any) {
      if (error.response) {
        toast.error(`Error: ${error.response.status} - ${error.response.statusText}`);
      } else {
        toast.error(t('webhookTest.cannotConnect'));
      }
    } finally {
      setLoading(false);
    }
  };

  const verifyWebhookSignature = async () => {
    if (!verifyPayload || !verifySignature || !verifySecret) {
      toast.error(t('webhookTest.fillAllFields'));
      return;
    }

    try {
      const payload = JSON.parse(verifyPayload);
      const response = await axios.post(`${API_BASE_URL}/webhook-test/verify-signature`, {
        payload,
        signature: verifySignature,
        secret: verifySecret
      });

      setVerifyResult(response.data.data);
      
      if (response.data.data.isValid) {
        toast.success(t('webhookTest.signatureValid'));
      } else {
        toast.error(t('webhookTest.signatureInvalid'));
      }
    } catch (error: any) {
      toast.error(t('common.error'));
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t('common.copied'));
    } catch (err) {
      toast.error(t('common.cannotCopy'));
    }
  };

  const formatJson = (obj: any) => {
    try {
      return JSON.stringify(obj, null, 2);
    } catch {
      return String(obj);
    }
  };

  return (
    <div className="w-full">
      {/* Header */}
      <div 
        className="text-white px-6 py-5 rounded-2xl mb-6 shadow-lg flex items-center gap-4"
        style={{ background: 'var(--theme-primary-gradient)' }}
      >
        <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center text-xl">
          <FontAwesomeIcon icon={faFlask} />
        </div>
        <div>
          <h1 className="text-2xl font-bold">Webhook Test Receiver</h1>
          <p className="text-white/70 text-sm">{t('webhookTest.subtitle')}</p>
        </div>
      </div>

      {/* Webhook URL Display */}
      <div className="bg-white rounded-2xl shadow-theme p-6 mb-6">
        <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2 mb-4">
          <FontAwesomeIcon icon={faLocationDot} className="text-red-500" /> Webhook Test URL
        </h2>
        <div className="flex gap-2 items-center">
          <code className="flex-1 bg-gray-100 px-4 py-3 rounded-lg text-gray-800 font-mono text-sm overflow-x-auto">
            {webhookUrl}
          </code>
          <button 
            onClick={() => copyToClipboard(webhookUrl)} 
            className="px-4 py-3 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors flex items-center gap-2"
          >
            <FontAwesomeIcon icon={faCopy} /> Copy
          </button>
        </div>
        <p className="text-gray-500 text-sm mt-2">
          {t('webhookTest.urlDescription')}
        </p>
      </div>

      {/* Send Test Webhook */}
      <div className="bg-white rounded-2xl shadow-theme p-6 mb-6">
        <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2 mb-4">
          <FontAwesomeIcon icon={faPaperPlane} className="text-blue-500" /> {t('webhookTest.sendTestWebhook')}
        </h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Target URL:</label>
            <input
              type="text"
              value={webhookUrl}
              onChange={(e) => setWebhookUrl(e.target.value)}
              placeholder="http://localhost:5000/api/webhook-test/receive"
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Payload (JSON):</label>
            <textarea
              value={testPayload}
              onChange={(e) => setTestPayload(e.target.value)}
              rows={12}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary font-mono text-sm"
            />
          </div>
          <button 
            onClick={sendTestWebhook} 
            className="px-6 py-3 text-white rounded-xl font-medium transition-all flex items-center gap-2"
            style={{ background: 'var(--theme-primary-gradient)' }}
            disabled={loading}
          >
            {loading ? (
              <><FontAwesomeIcon icon={faClock} className="animate-spin" /> {t('webhookTest.sending')}</>
            ) : (
              <><FontAwesomeIcon icon={faPaperPlane} /> {t('webhookTest.sendTestWebhook')}</>
            )}
          </button>
        </div>
      </div>

      {/* Logs Section */}
      <div className="bg-white rounded-2xl shadow-theme p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
            <FontAwesomeIcon icon={faClipboardList} className="text-indigo-500" /> 
            Received Webhooks ({logs.length})
          </h2>
          <div className="flex gap-2 items-center">
            <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                className="rounded text-primary focus:ring-primary"
              />
              Auto Refresh (3s)
            </label>
            <button 
              onClick={loadLogs} 
              className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors text-sm flex items-center gap-1"
            >
              <FontAwesomeIcon icon={faRotate} /> Refresh
            </button>
            <button 
              onClick={clearLogs} 
              className="px-3 py-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg transition-colors text-sm flex items-center gap-1"
            >
              <FontAwesomeIcon icon={faTrash} /> Clear All
            </button>
          </div>
        </div>

        {logs.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <FontAwesomeIcon icon={faClipboardList} className="text-4xl mb-3" />
            <p>{t('webhookTest.noWebhooksYet')}</p>
            <p className="text-sm">{t('webhookTest.noWebhooksHint')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="space-y-2 max-h-[500px] overflow-y-auto">
              {logs.map((log) => (
                <div
                  key={log.id}
                  className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    selectedLog?.id === log.id 
                      ? 'border-primary bg-primary/5' 
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  } ${log.error ? 'border-red-200 bg-red-50' : ''}`}
                  onClick={() => setSelectedLog(log)}
                >
                  <div className="text-xs text-gray-500 mb-1">
                    {new Date(log.receivedAt).toLocaleString('th-TH')}
                  </div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-bold">{log.method}</span>
                    {log.signature && (
                      <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs flex items-center gap-1">
                        <FontAwesomeIcon icon={faKey} /> Signed
                      </span>
                    )}
                    {log.error && (
                      <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs flex items-center gap-1">
                        <FontAwesomeIcon icon={faTimesCircle} /> Error
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-gray-700 truncate">
                    {log.body?.event || log.body?.order_number || 'Unknown payload'}
                  </div>
                </div>
              ))}
            </div>

            {selectedLog && (
              <div className="bg-gray-50 rounded-xl p-5 max-h-[500px] overflow-y-auto">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-gray-800 flex items-center gap-2">
                    <FontAwesomeIcon icon={faClipboardList} /> Webhook Details
                  </h3>
                  <button 
                    onClick={() => setSelectedLog(null)} 
                    className="w-8 h-8 bg-gray-200 hover:bg-gray-300 rounded-lg flex items-center justify-center"
                  >
                    <FontAwesomeIcon icon={faXmark} />
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-semibold text-gray-600 mb-1 flex items-center gap-1">
                      <FontAwesomeIcon icon={faClock} className="text-gray-400" /> Received At
                    </h4>
                    <code className="text-sm text-gray-800">{new Date(selectedLog.receivedAt).toLocaleString('th-TH')}</code>
                  </div>

                  <div>
                    <h4 className="text-sm font-semibold text-gray-600 mb-1 flex items-center gap-1">
                      <FontAwesomeIcon icon={faEnvelope} className="text-gray-400" /> Headers
                    </h4>
                    <pre className="bg-gray-900 text-green-400 p-3 rounded-lg text-xs overflow-x-auto">{formatJson(selectedLog.headers)}</pre>
                  </div>

                  {selectedLog.signature && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-600 mb-1 flex items-center gap-1">
                        <FontAwesomeIcon icon={faKey} className="text-gray-400" /> Signature
                      </h4>
                      <code className="block bg-amber-100 text-amber-800 p-3 rounded-lg text-xs break-all">{selectedLog.signature}</code>
                    </div>
                  )}

                  <div>
                    <h4 className="text-sm font-semibold text-gray-600 mb-1">Body</h4>
                    <pre className="bg-gray-900 text-green-400 p-3 rounded-lg text-xs overflow-x-auto">{selectedLog.rawBody}</pre>
                    <button 
                      onClick={() => copyToClipboard(selectedLog.rawBody)} 
                      className="mt-2 text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
                    >
                      <FontAwesomeIcon icon={faCopy} /> Copy
                    </button>
                  </div>

                  {selectedLog.queryParams && Object.keys(selectedLog.queryParams).length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-600 mb-1 flex items-center gap-1">
                        <FontAwesomeIcon icon={faLink} className="text-gray-400" /> Query Parameters
                      </h4>
                      <pre className="bg-gray-900 text-blue-400 p-3 rounded-lg text-xs overflow-x-auto">{formatJson(selectedLog.queryParams)}</pre>
                    </div>
                  )}

                  <div>
                    <h4 className="text-sm font-semibold text-gray-600 mb-1 flex items-center gap-1">
                      <FontAwesomeIcon icon={faGlobe} className="text-gray-400" /> Request Info
                    </h4>
                    <p className="text-sm text-gray-700"><strong>Method:</strong> {selectedLog.method}</p>
                    <p className="text-sm text-gray-700"><strong>IP Address:</strong> {selectedLog.ip || 'Unknown'}</p>
                  </div>

                  {selectedLog.error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                      <h4 className="text-sm font-semibold text-red-700 mb-1 flex items-center gap-1">
                        <FontAwesomeIcon icon={faTimesCircle} /> Error
                      </h4>
                      <code className="text-red-600 text-sm">{selectedLog.error}</code>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Signature Verification Tool */}
      <div className="bg-white rounded-2xl shadow-theme p-6 mb-6">
        <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2 mb-4">
          <FontAwesomeIcon icon={faLock} className="text-amber-500" /> Signature Verification Tool
        </h2>
        <p className="text-gray-500 text-sm mb-4">{t('webhookTest.verifySignatureDesc')}</p>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Payload (JSON):</label>
            <textarea
              value={verifyPayload}
              onChange={(e) => setVerifyPayload(e.target.value)}
              rows={6}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary font-mono text-sm"
              placeholder='{"event":"order.paid","order_number":"ORD123"}'
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('webhookTest.signatureReceived')}:</label>
            <input
              type="text"
              value={verifySignature}
              onChange={(e) => setVerifySignature(e.target.value)}
              placeholder="abc123def456..."
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary mb-4"
            />
            <label className="block text-sm font-medium text-gray-700 mb-2">Webhook Secret:</label>
            <input
              type="text"
              value={verifySecret}
              onChange={(e) => setVerifySecret(e.target.value)}
              placeholder="your-webhook-secret"
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
          </div>
          <div className="flex flex-col justify-end">
            <button 
              onClick={verifyWebhookSignature} 
              className="px-6 py-3 text-white rounded-xl font-medium transition-all flex items-center justify-center gap-2 mb-4"
              style={{ background: 'var(--theme-primary-gradient)' }}
            >
              <FontAwesomeIcon icon={faCheckCircle} /> Verify Signature
            </button>

            {verifyResult && (
              <div className={`p-4 rounded-xl ${verifyResult.isValid ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                <h3 className={`font-bold flex items-center gap-2 mb-2 ${verifyResult.isValid ? 'text-green-700' : 'text-red-700'}`}>
                  <FontAwesomeIcon icon={verifyResult.isValid ? faCheckCircle : faTimesCircle} />
                  {verifyResult.isValid ? t('webhookTest.signatureValid') : t('webhookTest.signatureInvalid')}
                </h3>
                <p className="text-xs text-gray-600"><strong>Expected:</strong></p>
                <code className="text-xs break-all">{verifyResult.expectedSignature}</code>
                <p className="text-xs text-gray-600 mt-2"><strong>Received:</strong></p>
                <code className="text-xs break-all">{verifyResult.receivedSignature}</code>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Documentation */}
      <div className="bg-white rounded-2xl shadow-theme p-6">
        <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2 mb-4">
          <FontAwesomeIcon icon={faFile} className="text-purple-500" /> Documentation
        </h2>
        
        <div className="space-y-4">
          <div>
            <h3 className="font-semibold text-gray-700 mb-2">{t('webhookTest.howToUse')}</h3>
            <ol className="list-decimal list-inside text-gray-600 text-sm space-y-1">
              <li>{t('webhookTest.step1')}</li>
              <li>{t('webhookTest.step2')}</li>
              <li>{t('webhookTest.step3')}</li>
              <li>{t('webhookTest.step4')}</li>
            </ol>
          </div>

          <div>
            <h3 className="font-semibold text-gray-700 mb-2">{t('webhookTest.sendWithCurl')}</h3>
            <div className="bg-gray-900 rounded-xl p-4 overflow-x-auto">
              <pre className="text-green-400 text-sm">{`curl -X POST ${webhookUrl} \\
  -H "Content-Type: application/json" \\
  -H "X-Webhook-Signature: your-signature" \\
  -d '{
    "event": "order.paid",
    "order_number": "ORD123",
    "status": "paid"
  }'`}</pre>
            </div>
          </div>

          <div>
            <h3 className="font-semibold text-gray-700 mb-2">Response Format</h3>
            <p className="text-gray-600 text-sm mb-2">{t('webhookTest.responseDesc')}</p>
            <div className="bg-gray-900 rounded-xl p-4">
              <pre className="text-blue-400 text-sm">{`{
  "success": true,
  "message": "Webhook received successfully",
  "receivedAt": "2025-10-10T...",
  "dataReceived": {
    "bodyKeys": ["event", "order_number", "status"],
    "bodySize": 123
  }
}`}</pre>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WebhookTestPage;
